/**
 * ShortCallBanner Component
 *
 * Informational banner explaining why a short call — or chat — has no
 * analysis (and possibly no transcript) — shown instead of a generic "still
 * processing" message, since these outputs are never coming.
 *
 * Two cases (call variant, the default):
 * - `transcribed` (default): duration < project's minCallDurationSeconds. The
 *   call WAS transcribed but not analyzed (too little content for QA).
 * - `transcribed={false}`: the call was too short to even transcribe (under the
 *   ingest minimum, ~15s) — neither transcript nor analysis exists. Shown on
 *   BOTH the transcript and analysis panels.
 *
 * `variant="chat"` replaces the seconds-based wording and the duration meta
 * row with the chat's own reason (no operator reply, or too few messages) —
 * a chat's text always arrives already transcribed from the CRM, so the
 * `transcribed` prop does not apply to it.
 */

import { Clock, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_MIN_CALL_DURATION_SECONDS } from '@aica/core';
import { Card } from '@/components/ui/card';
import { formatDuration } from '@/lib/format-duration';

export interface ShortCallBannerProps {
  /** Duration of the call in seconds */
  durationSeconds?: number;
  /** Minimum required duration (from project settings) */
  minDurationSeconds?: number;
  /**
   * Whether the call was transcribed. Default true (transcribed but not
   * analyzed). When false, the call was too short to transcribe at all —
   * neither transcript nor analysis is available. Call-only.
   */
  transcribed?: boolean;
  /**
   * Что за запись. У звонка мерило — секунды, у чата — реплики, и текст
   * «звонок короче 30 секунд» на чате читается как ошибка продукта.
   */
  variant?: 'call' | 'chat';
  /** Additional CSS classes */
  className?: string;
}

export function ShortCallBanner({
  durationSeconds,
  minDurationSeconds = DEFAULT_MIN_CALL_DURATION_SECONDS,
  transcribed = true,
  variant = 'call',
  className,
}: ShortCallBannerProps) {
  const { t } = useTranslation(['records', 'common']);
  const isChat = variant === 'chat';

  const formatSpan = (seconds: number | undefined): string =>
    seconds === undefined ? '?' : formatDuration(seconds);

  return (
    <Card
      className={`border-border bg-info-dim ${className ?? ''}`}
      data-testid="short-call-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-10 h-10 rounded-full bg-info-dim flex items-center justify-center">
            <Clock className="h-5 w-5 text-info" />
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold text-fg">
              {isChat
                ? t('records.shortChat.title', 'Short Chat')
                : t('records.shortCall.title', 'Короткий дзвінок')}
            </h4>
            <Info className="h-4 w-4 text-fg-muted" />
          </div>
          <p className="text-sm text-fg-muted">
            {isChat
              ? t(
                  'records.shortChat.description',
                  'This chat has no operator reply, or too few messages — no analysis was performed.'
                )
              : transcribed
                ? t(
                    'records.shortCall.description',
                    'Цей дзвінок занадто короткий для повноцінного аналізу якості. Мінімальна тривалість для аналізу: {{minDuration}}.',
                    { minDuration: formatSpan(minDurationSeconds) }
                  )
                : t(
                    'records.shortCall.skippedDescription',
                    'This call is too short to transcribe or analyze.'
                  )}
          </p>
          {/* Duration is a call-only metric — a chat's significance filter counts
              messages, not seconds, so this row would be meaningless there. */}
          {!isChat && (
            <div className="flex items-center gap-4 text-xs text-fg-subtle">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t('records.shortCall.duration', 'Тривалість')}:{' '}
                {formatSpan(durationSeconds)}
              </span>
              {transcribed && (
                <span>
                  {t('records.shortCall.minimum', 'Мінімум')}:{' '}
                  {formatSpan(minDurationSeconds)}
                </span>
              )}
            </div>
          )}
          {!isChat && (
            <div className="mt-3 p-2 bg-bg-surface rounded-card border border-border">
              <p className="text-xs text-fg-muted">
                {transcribed ? (
                  <>
                    <span className="font-medium text-fg">
                      {t('records.shortCall.whatIsAvailable', 'Що доступно')}:
                    </span>{' '}
                    {t(
                      'records.shortCall.availableFeatures',
                      'Транскрипція дзвінка доступна на вкладці "Транскрипт". Аналіз якості та перевірка скрипта пропущені через недостатній обсяг розмови.'
                    )}
                  </>
                ) : (
                  t(
                    'records.shortCall.skippedNote',
                    'Calls below the minimum duration are saved without a transcript or quality analysis.'
                  )
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
